import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { FiRefreshCw } from 'react-icons/fi';
import { getToken, isAuthenticated, consumePostLoginPath } from '../services/authService';
import { apifetch } from '../services/interceptors';
import AuthShell, { AuthField, PasswordInput } from '../v2/auth/AuthShell';
import { TbKey } from 'react-icons/tb';
import { browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { signInWithPasskey, fetchPasskeyChallenge, passkeyErrorMessage, CHALLENGE_STALE_MS } from '../v2/hooks/usePasskeys';
import { completeMicrosoftPopup, startMicrosoftPopup } from '../utils/portalMicrosoftSignIn';
const Login = () => {
    const [error, setError] = useState('');
    const [step2, setStep2] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [code, setCode] = useState('');
    const [mfaMethod, setMfaMethod] = useState('email'); // 'email' | 'totp'
    const [timer, setTimer] = useState(900); // 15 minutes
    const [isCodeExpired, setIsCodeExpired] = useState(false);
    const [isCodeSent, setIsCodeSent] = useState(false);
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [ssoEnabled, setSsoEnabled] = useState(false);
    const [ssoPending, setSsoPending] = useState(false);
    const cancelMicrosoftPopup = useRef(null);
    const [passkeyEnabled, setPasskeyEnabled] = useState(false);
    const [passkeyBusy, setPasskeyBusy] = useState(false);
    // Held in a ref, not state: it is read inside the click handler and must never
    // trigger a re-render, which would itself break the user-activation window.
    const passkeyChallenge = useRef(null);

    const navigate = useNavigate();
    useEffect(() => () => cancelMicrosoftPopup.current?.(), []);
    const microsoftMessages = {
        no_tenant: "Your organization isn't registered with Pipsqueak yet — please ask your administrator to sign up first.",
        no_email: "Microsoft didn't share a usable email address for your account. Please contact your administrator.",
        disabled: 'Microsoft sign-in is not enabled for this platform.',
        auth_failed: 'Microsoft sign-in failed or was cancelled. Please try again.',
        popup_blocked: 'The sign-in window could not open. Allow popups for Pipsqueak, then try again.',
        cancelled: 'Microsoft sign-in was cancelled. You can try again.',
        timed_out: 'Microsoft sign-in timed out. Please try again.'
    };

    // Step 2 is a dead end until the field has focus, and the user has just been moved
    // to a screen they did not navigate to themselves — so put the caret where the only
    // thing they can do is. A ref rather than the autoFocus attribute: the input is not
    // remounted when the method flips between email and authenticator, so autoFocus
    // would fire once and never again.
    const codeRef = useRef(null);
    useEffect(() => {
        if (step2) codeRef.current?.focus();
    }, [step2]);

    // countdown for verification code
    useEffect(() => {
        let countdown;
        if (isCodeSent && timer > 0) {
            countdown = setInterval(() => {
                setTimer((prev) => {
                    if (prev <= 1) {
                        clearInterval(countdown);
                        setIsCodeExpired(true);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => clearInterval(countdown);
    }, [isCodeSent, timer]);

    const handleResendCode = async (e) => {
        e.preventDefault();
        setError('');
        setMessage('');
        try {
            const { data } = await axios.post('s1/api/resend-login', { email });
            setIsCodeExpired(false);
            setTimer(900);
            setIsCodeSent(true);
            setMessage(data.message);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to resend code. Try again.');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setMessage('');
        setLoading(true);

        try {
            const token = getToken();
            const response = await apifetch('s1/api/login', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ email, password }),
            });

            const data = await response.json();

            if (!response.ok) {
                setError(data.error || 'Unexpected error occurred!');
            } else {
                setStep2(true);
                setMessage(data.message);
                setMfaMethod(data.mfaMethod === 'totp' ? 'totp' : 'email');
                setIsCodeExpired(false);
                setTimer(900);
                setIsCodeSent(true);
            }
        } catch (err) {
            setError(err.message || 'Something went wrong, please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const handleVerify = async (e) => {
        e.preventDefault();
        setError('');
        setMessage('');
        setLoading(true);

        try {
            const { data } = await axios.post('s1/api/verify-login', { email, password, code });
            localStorage.setItem('token', data.token);
            setMessage(data.message);
            navigate(consumePostLoginPath());
        } catch (err) {
            setError(err.response?.data?.error || 'Something went wrong, please try again later.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isAuthenticated()) {
            navigate(consumePostLoginPath());
        }
    }, [navigate]);

    // Show the "Sign in with Microsoft" button only when the platform has Entra configured.
    useEffect(() => {
        axios.get('s1/api/auth/entra/enabled')
            .then(({ data }) => setSsoEnabled(Boolean(data && data.enabled)))
            .catch(() => setSsoEnabled(false));
    }, []);

    // Both halves have to be true to offer a passkey: the server needs an RP ID
    // configured, and the browser needs to support WebAuthn at all. Offering it otherwise
    // produces a button that can only fail.
    useEffect(() => {
        if (!browserSupportsWebAuthn()) return;
        axios.get('s1/api/passkeys/enabled')
            .then(({ data }) => setPasskeyEnabled(Boolean(data && data.enabled)))
            .catch(() => setPasskeyEnabled(false));
    }, []);

    // Fetch the challenge BEFORE the user clicks.
    //
    // navigator.credentials.get() is only allowed while the browser still holds the
    // transient user activation from the click, and awaiting a network round trip first
    // can spend it. Safari then throws NotAllowedError — the same error as a cancelled
    // prompt — so the button appeared to do nothing at all.
    const primePasskey = useCallback(() => {
        fetchPasskeyChallenge()
            .then((c) => { passkeyChallenge.current = { ...c, at: Date.now() }; })
            .catch(() => { passkeyChallenge.current = null; });
    }, []);

    useEffect(() => {
        if (!passkeyEnabled) return;
        primePasskey();
        // Challenges are single-use and expire in five minutes, so a login page left
        // open on a second monitor still has a live one when it is finally clicked.
        const timer = setInterval(primePasskey, CHALLENGE_STALE_MS);
        return () => clearInterval(timer);
    }, [passkeyEnabled, primePasskey]);

    const handlePasskeySignIn = async () => {
        setError('');
        setPasskeyBusy(true);
        // Only a challenge that is still fresh; a stale one would be rejected by the
        // server after the user had already authenticated, which is the worst moment to
        // find out. Passing undefined makes signInWithPasskey fetch inline instead.
        const primed = passkeyChallenge.current;
        const fresh = primed && Date.now() - primed.at < CHALLENGE_STALE_MS ? primed : undefined;
        // Single-use either way, so it is spent the moment it is handed over.
        passkeyChallenge.current = null;
        try {
            // This form has no "remember me" — the password path issues a 1-day token
            // and the passkey path matches it rather than inventing a longer session.
            const { token } = await signInWithPasskey({ prefetched: fresh });
            localStorage.setItem('token', token);
            navigate(consumePostLoginPath());
        } catch (err) {
            const message = err?.response?.data?.error || passkeyErrorMessage(err, 'login');
            if (message) setError(message);
            primePasskey();   // the one just used is spent, ready the next attempt
        } finally {
            setPasskeyBusy(false);
        }
    };

    // Handle the return from Microsoft SSO: a JWT arrives in the URL fragment (kept out of
    // logs/Referer), errors arrive as a query param.
    useEffect(() => {
        const hash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : '';
        const token = new URLSearchParams(hash).get('sso_token');
        if (token) {
            if (completeMicrosoftPopup({ token })) return;
            localStorage.setItem('token', token);
            window.history.replaceState(null, '', window.location.pathname);
            navigate(consumePostLoginPath());
            return;
        }
        const code = new URLSearchParams(window.location.search).get('sso_error');
        if (code) {
            if (completeMicrosoftPopup({ error: code })) return;
            const messages = {
                no_tenant: "Your organization isn't registered with Pipsqueak yet — please ask your administrator to sign up first.",
                no_email: "Microsoft didn't share a usable email address for your account. Please contact your administrator.",
                disabled: 'Microsoft sign-in is not enabled for this platform.',
                auth_failed: 'Microsoft sign-in failed or was cancelled. Please try again.'
            };
            setError(messages[code] || 'Microsoft sign-in failed. Please try again.');
            window.history.replaceState(null, '', window.location.pathname);
        }
    }, [navigate]);

    const handleMicrosoftSignIn = () => {
        if (window.self !== window.top) {
            if (cancelMicrosoftPopup.current || ssoPending) return;
            setError('');
            const cancel = startMicrosoftPopup({
                onSuccess: token => {
                    cancelMicrosoftPopup.current = null;
                    localStorage.setItem('token', token);
                    navigate(consumePostLoginPath());
                },
                onError: code => { cancelMicrosoftPopup.current = null; setError(microsoftMessages[code] || microsoftMessages.auth_failed); },
                onPending: setSsoPending
            });
            // A blocked popup completes synchronously; retain no stale cancel handle.
            cancelMicrosoftPopup.current = cancel;
            return;
        }
        window.location.href = '/s1/api/auth/entra/start';
    };

    return (
        <AuthShell>
            {!step2 ? (
                <>
                    <span className="auth-eyebrow">Welcome back</span>
                    <h1 className="auth-title">Sign in to Pipsqueak</h1>
                    <p className="auth-sub">
                        Use your work email. You'll confirm with a second factor on the next step.
                    </p>

                    <form onSubmit={handleSubmit} className="auth-fields">
                        <AuthField label="Email" required>
                            <input
                                className="auth-input"
                                type="email"
                                autoComplete="username"
                                placeholder="you@company.com"
                                value={email}
                                onChange={(e) => { setError(''); setMessage(''); setEmail(e.target.value); }}
                                required
                            />
                        </AuthField>

                        <AuthField label="Password" required>
                            <PasswordInput
                                autoComplete="current-password"
                                placeholder="Your password"
                                value={password}
                                onChange={(e) => { setError(''); setMessage(''); setPassword(e.target.value); }}
                                required
                            />
                        </AuthField>

                        {error && <div className="auth-note">{error}</div>}

                        <div className="auth-meta"><Link to="/forgetpass">Forgot your password?</Link></div>

                        <button type="submit" className="auth-btn" disabled={loading}>
                            {loading ? 'Signing in…' : 'Sign in'}
                        </button>
                    </form>

                    {(passkeyEnabled || ssoEnabled) && (
                        <div className="auth-divider" style={{ margin: '1.25rem 0' }}>or</div>
                    )}

                    {passkeyEnabled && (
                        <button type="button" className="auth-btn auth-btn--ghost"
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '.5rem' }}
                                disabled={passkeyBusy}
                                onClick={handlePasskeySignIn}>
                            <TbKey size={18} aria-hidden="true" />
                            {passkeyBusy ? 'Waiting for your device…' : 'Sign in with a passkey'}
                        </button>
                    )}

                    {ssoEnabled && (
                        <>
                            <button type="button" className="auth-btn auth-btn--ghost"
                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '.5rem',
                                             marginTop: passkeyEnabled ? '.5rem' : 0 }}
                                    onClick={handleMicrosoftSignIn} disabled={ssoPending}>
                                <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
                                    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                                    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                                    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                                    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
                                </svg>
                                {ssoPending ? 'Waiting for Microsoft sign-in…' : 'Sign in with Microsoft'}
                            </button>
                        </>
                    )}

                    <p className="auth-foot" style={{ marginTop: '1.5rem' }}>
                        New here? <Link to="/register">Create an account</Link>
                    </p>
                </>
            ) : (
                <>
                    <span className="auth-eyebrow">Step 2 of 2</span>
                    <h1 className="auth-title">Confirm it's you</h1>
                    <p className="auth-sub">
                        {mfaMethod === 'totp'
                            ? 'Enter the current code from your authenticator app, or a recovery code.'
                            : `We sent a six-digit code to ${email}.`}
                    </p>

                    {message && <div className="auth-note auth-note--ok" style={{ marginBottom: '1.25rem' }}>{message}</div>}

                    <form onSubmit={handleVerify} className="auth-fields">
                        <AuthField
                            label={mfaMethod === 'totp' ? 'Authenticator code' : 'Verification code'}
                            required
                            hint={mfaMethod === 'totp'
                                ? 'Google Authenticator, Microsoft Authenticator, 1Password or Duo.'
                                : undefined}>
                            <div style={{ display: 'flex', gap: '.5rem' }}>
                                <input
                                    ref={codeRef}
                                    className="auth-input"
                                    type="text"
                                    autoComplete="one-time-code"
                                    inputMode={mfaMethod === 'totp' ? 'text' : 'numeric'}
                                    placeholder={mfaMethod === 'totp' ? '6-digit or recovery code' : '000000'}
                                    value={code}
                                    onChange={(e) => setCode(e.target.value)}
                                    required
                                />
                                {mfaMethod !== 'totp' && (
                                    <button type="button" className="auth-btn auth-btn--ghost"
                                            style={{ width: 'auto', flex: 'none', display: 'inline-flex', alignItems: 'center', gap: '.375rem' }}
                                            onClick={handleResendCode} disabled={!isCodeExpired}>
                                        <FiRefreshCw /> Resend
                                    </button>
                                )}
                            </div>
                        </AuthField>

                        {/* The countdown is the reason Resend is disabled, so it sits with it
                            rather than at the foot of the card. */}
                        {mfaMethod !== 'totp' && (
                            isCodeExpired
                                ? <p className="auth-error">That code has expired. Send another.</p>
                                : <p className="auth-help">
                                      Expires in {Math.floor(timer / 60)}:{('0' + (timer % 60)).slice(-2)}
                                  </p>
                        )}

                        {error && <div className="auth-note">{error}</div>}

                        <button type="submit" className="auth-btn" disabled={loading}>
                            {loading ? 'Verifying…' : 'Verify'}
                        </button>
                    </form>

                    <p className="auth-foot" style={{ marginTop: '1.5rem' }}>
                        <Link to="/login" onClick={() => setStep2(false)}>Use a different account</Link>
                    </p>
                </>
            )}
        </AuthShell>
    );
};

export default Login;
